/**
 * LocalStore is the narrow persistence seam. The in-memory and SQLite/OPFS
 * adapters implement the same interface; UI and feature code never touch it
 * directly (they go through repositories + services).
 *
 * It deliberately exposes no `delete`. Removal is modelled as a lifecycle
 * transition on the record itself, preserving the no-deletion invariant.
 */
export interface LocalStore {
  get<T>(collection: string, id: string): Promise<T | undefined>;
  put<T>(collection: string, id: string, value: T): Promise<void>;
  list<T>(collection: string): Promise<readonly T[]>;
  /** Key-faithful listing: [id, value] pairs, for backup/restore and migration. */
  entries<T>(collection: string): Promise<readonly (readonly [string, T])[]>;
  count(collection: string): Promise<number>;
}

/** Simple, dependency-free in-memory store used as the reference adapter + in tests. */
export class MemoryLocalStore implements LocalStore {
  private readonly collections = new Map<string, Map<string, unknown>>();

  private col(collection: string): Map<string, unknown> {
    let c = this.collections.get(collection);
    if (!c) {
      c = new Map();
      this.collections.set(collection, c);
    }
    return c;
  }

  async get<T>(collection: string, id: string): Promise<T | undefined> {
    return this.col(collection).get(id) as T | undefined;
  }

  async put<T>(collection: string, id: string, value: T): Promise<void> {
    this.col(collection).set(id, value);
  }

  async list<T>(collection: string): Promise<readonly T[]> {
    return [...this.col(collection).values()] as T[];
  }

  async entries<T>(collection: string): Promise<readonly (readonly [string, T])[]> {
    return [...this.col(collection).entries()] as (readonly [string, T])[];
  }

  async count(collection: string): Promise<number> {
    return this.col(collection).size;
  }
}

/** Canonical collection names (one per entity kind + the mutation queue). */
export const Collections = {
  sourceRecords: 'source_records',
  captureItems: 'capture_items',
  tasks: 'tasks',
  reviews: 'reviews',
  reminders: 'reminders',
  targets: 'targets',
  evidence: 'consistency_evidence',
  habits: 'habits',
  habitCheckins: 'habit_checkins',
  routines: 'routines',
  problems: 'problems',
  projects: 'projects',
  calendarEvents: 'calendar_events',
  accounts: 'accounts',
  transactions: 'transactions',
  importBatches: 'import_batches',
  notes: 'notes',
  goals: 'goals',
  milestones: 'milestones',
  healthLogs: 'health_logs',
  studySubjects: 'study_subjects',
  studySessions: 'study_sessions',
  opportunities: 'opportunities',
  journalEntries: 'journal_entries',
  contentItems: 'content_items',
  ventures: 'ventures',
  inventoryItems: 'inventory_items',
  focusSessions: 'focus_sessions',
  directionItems: 'direction_items',
  profiles: 'profiles',
  admissions: 'admissions',
  fundingSources: 'funding_sources',
  educationPrograms: 'education_programs',
  resumeProfiles: 'resume_profiles',
  securityConfig: 'security_config',
  securityEvents: 'security_events',
  mutations: 'mutation_queue',
  conflicts: 'conflict_reviews',
  events: 'event_history',
  syncMeta: 'sync_meta',
  schemaMeta: 'schema_meta',
  relationships: 'relationships',
  relationshipTombstones: 'relationship_tombstones',
  deliveries: 'notification_deliveries',
  otpChallenges: 'otp_challenges',
} as const;
